/** Реклама: только гостевые экраны сайта, без Вебвизора и содержимого приложения. */
import { isDesktop } from '@/lib/desktop'
import { isStaging } from '@/lib/stage'

export const METRIKA_COUNTER = 113343003
export type MarketingPage = '/' | '/login' | '/register'
type Goal = 'register_open' | 'registration_success' | 'download_app'
type MetrikaCall = ((counter: number, command: string, ...args: unknown[]) => void) & { a?: unknown[][]; l?: number }
declare global { interface Window { ym?: MetrikaCall } }

let active = false
let lastPage: MarketingPage | null = null
let campaignQuery = ''
const SCRIPT_ID = 'klichat-metrika'

function enabled(): boolean {
  return import.meta.env.PROD && !isStaging && !isDesktop &&
    location.protocol === 'https:' && ['klichat.ru', 'www.klichat.ru'].includes(location.hostname)
}

/** До catchCampaign(): он очищает адрес рекламной ссылки. Сохраняем только рекламные метки. */
export function captureMetrikaCampaign(): void {
  const source = new URLSearchParams(location.search)
  const query = new URLSearchParams()
  for (const key of ['yclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'c']) {
    const value = source.get(key)
    if (value && value.length <= 256) query.set(key, value)
  }
  campaignQuery = query.toString()
}

function pageUrl(page: MarketingPage): string {
  return `${location.origin}${page}${campaignQuery ? '?' + campaignQuery : ''}`
}

function referrer(): string {
  try { return new URL(document.referrer).origin + '/' } catch { return '' }
}

function send(command: string, ...args: unknown[]): void {
  try { window.ym?.(METRIKA_COUNTER, command, ...args) } catch { /* реклама не мешает регистрации */ }
}

function start(page: MarketingPage): void {
  if (active) return
  try {
    if (!window.ym) {
      const queued: MetrikaCall = (...args) => { (queued.a ??= []).push(args) }
      queued.l = Date.now()
      window.ym = queued
    }
    active = true
    send('init', {
      defer: true, url: pageUrl(page), referrer: referrer(),
      webvisor: false, clickmap: false, trackLinks: false, accurateTrackBounce: true,
    })
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = SCRIPT_ID
      script.async = true
      script.src = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_COUNTER}`
      script.onerror = () => {
        script.remove()
        // Сбросить несостоявшуюся загрузку; следующий гостевой экран может повторить её.
        if (window.ym?.a) window.ym.a.length = 0
        active = false
        lastPage = null
      }
      document.head.appendChild(script)
    }
  } catch {
    active = false
    lastPage = null
  }
}

/** Виртуальные просмотры нужны и при переключении формы без изменения URL. */
export function trackMarketingPage(page: MarketingPage): void {
  if (!enabled() || lastPage === page) return
  start(page)
  if (!active) return
  lastPage = page
  send('hit', pageUrl(page), { title: page === '/' ? 'Кличат' : page === '/register' ? 'Регистрация · Кличат' : 'Вход · Кличат', referer: referrer() })
  if (page === '/register') trackMarketingGoal('register_open')
}

export function trackMarketingGoal(goal: Goal, params?: { platform: string }): void {
  if (!enabled() || !active) return
  if (params) send('reachGoal', goal, params)
  else send('reachGoal', goal)
}

export function trackAppDownload(url: string): void {
  const file = url.split('/').pop()
  const platform = file === 'klichat-win-setup.exe' ? 'windows' : file === 'klichat-mac-arm64.dmg' ? 'mac-arm64' : file === 'klichat-mac-x64.dmg' ? 'mac-intel' : null
  if (platform) trackMarketingGoal('download_app', { platform })
}

/** Остановить счётчик до открытия чатов, каталога, восстановления или почтовых действий. */
export function stopMetrika(): void {
  if (active) send('destruct')
  active = false
  lastPage = null
}
